const Q = require('q');
const _ = require('underscore');
const model = require('model/model');
const { v4: uuidv4 } = require('uuid');

const assignmentService = require('services/assignmentService');
const classService = require('services/classService');

const getBoK = aid => {
	let prms = Q.reject();
	if (aid) {
		prms = assignmentService.getBokForAssignment({
			aid
		});
	}
	return prms;
};

const getTeacherCbok = aid => {
	return assignmentService
		.getTeacherBookOfKnowledge({
			aid
		})
		.then(response => {
			return response;
		});
};

const get = aid => {
	return Q.all([
		classService.getClassBoKEntries({
			aid
		}),
		getTeacherCbok(aid)
	]).spread((studentCbok, teacherCbok) => {
		let prms = Q();
		let classBok = _.assign({}, model.get('classBok') || {});
		let content = [];
		studentCbok = studentCbok || [];
		teacherCbok = teacherCbok || {};

		// If teacher entries, push to cbok
		if (teacherCbok && teacherCbok.entries) {
			let entriesByArticle = Object.keys(teacherCbok.entries);
			entriesByArticle.forEach(cpid => {
				let entriesById = Object.keys(teacherCbok.entries[cpid]);
				entriesById.forEach(id => {
					let entry = teacherCbok.entries[cpid] && teacherCbok.entries[cpid][id];
					let suids = entry && entry.suids && Object.keys(entry.suids);
					content.push(
						_.assign({}, entry, {
							id,
							cpid,
							aid,
							suids,
							b: entry.bok,
							s: 's',
							ts: entry.c
						})
					);
				});
			});
		}

		prms = getBoK(aid).then(bokWorkspaces => {
			let studentEntries = [];
			let studentEntriesMap = {};

			if (bokWorkspaces && bokWorkspaces.length) {
				bokWorkspaces.forEach(workspace => {
					if (workspace && workspace.uid && workspace.sc && workspace.sc.a) {
						let suid = workspace.uid;
						let entries = workspace.sc.a;
						studentEntriesMap[suid] = {};
						Object.keys(entries).forEach(cpid => {
							let e = entries[cpid];
							if (e.s === 's') {
								let improvedEntry = _.assign(
									{
										suid: suid,
										suids: [suid],
										cpid,
										aid,
										id: uuidv4()
									},
									e
								);
								studentEntries.push(improvedEntry);
								studentEntriesMap[suid][cpid] = improvedEntry;
							}
						});
					}
				});
			}
			studentCbok.forEach(cb => {
				let entryList = [];
				// Case of promoted entry
				if (cb.uid && cb.a && studentEntriesMap[cb.uid]) {
					// flatten student entries (expand "a")
					let rawEntries = Object.keys(cb.a);
					let studentMeta = _.assign({}, cb, {
						a: undefined
					});
					rawEntries.forEach(pid => {
						let bokEntry = studentEntriesMap[cb.uid][pid];
						if (bokEntry) {
							let tempEntry = _.assign({}, studentMeta, bokEntry);
							entryList.push(tempEntry);
						}
					});
				}

				content = content.concat(entryList);
			});

			classBok[aid] = {
				content,
				studentEntries
			};
			model.set('classBok', classBok);
		});

		return prms;
	});
};

const create = (aid, cc, uids, pid, entry, bok) => {
	let data = {
		aid
	};
	let prms = null;
	let uid = undefined;

	if (cc && !bok) {
		uid = uids[0];
		data.uid = uid;
		data.a = pid;
		data.cc = cc;
		prms = classService.addToClassBoK(data);
	} else {
		let suidsMap = uids.reduce((acc, suid) => {
			acc[suid] = true;
			return acc;
		}, {});
		data.suids = suidsMap;
		data.bok = bok;
		data.pid = pid;
		prms = assignmentService.createTeacherBookOfKnowledge(data);
	}

	return prms.then(response => {
		let classBok = model.get('classBok') || {};
		let aidClassBok = classBok[aid];
		let content = aidClassBok ? aidClassBok.content : null;

		if (content) {
			let id = entry.id;
			if (id === 'new' && response) {
				let entries = response.entries;
				if (entries && entries[pid]) {
					let ids = Object.keys(entries[pid]);
					id = ids.length === 1 ? ids[0] : uuidv4();
				}
			}

			let improvedData = {
				id: id || uuidv4(),
				cpid: pid,
				aid,
				suid: uid,
				suids: uid ? [uid] : uids,
				s: 's',
				b: bok || entry.b
			};
			let improvedEntry = _.assign({}, entry, improvedData);
			content = [improvedEntry, ...content];

			classBok[aid] = _.assign({}, classBok[aid], {
				content
			});

			classBok = _.assign({}, classBok);

			model.set('classBok', classBok);
		}
	});
};

const remove = (id, aid, pid, cc, uid) => {
	let data = {
		aid
	};
	let prms = null;

	if (uid) {
		data.cc = cc;
		data.uid = uid;
		data.a = pid;
		prms = classService.removeFromClassBoK(data);
	} else {
		data.id = id;
		data.pid = pid;
		prms = assignmentService.deleteTeacherBookOfKnowledge(data);
	}

	return prms.then(response => {
		let classBok = model.get('classBok') || {};
		let aidClassBok = classBok[aid];
		let content = aidClassBok ? aidClassBok.content : null;

		if (content && content.length) {
			content = content.filter(c => c.id !== id);

			classBok[aid] = _.assign({}, classBok[aid], {
				content
			});
			classBok = _.assign({}, classBok);

			model.set('classBok', classBok);
		}
		return response;
	});
};

const edit = (id, aid, suids = [], pid, bok) => {
	let suidsMap = suids.reduce((acc, suid) => {
		acc[suid] = true;
		return acc;
	}, {});
	let data = {
		id,
		aid,
		pid,
		bok,
		suids: suidsMap
	};

	return assignmentService.editTeacherBookOfKnowledge(data).then(() => {
		let classBok = model.get('classBok') || {};
		let aidClassBok = classBok[aid];
		let content = aidClassBok ? aidClassBok.content : null;

		if (content && content.length) {
			content = content.map(c => {
				if (c.id === id) {
					c = _.assign(
						{},
						{
							id,
							cpid: pid,
							aid,
							suids,
							b: bok,
							s: 's',
							ts: Date.now() / 1000
						}
					);
				}
				return c;
			});

			classBok[aid] = _.assign({}, classBok[aid], {
				content
			});
			classBok = _.assign({}, classBok);

			model.set('classBok', classBok);
		}
	});
};

const getStudentEntries = (cc, suid) => {
	let data = {
		cc
	};

	if (suid) {
		data.uid = suid;
	}
	return classService.getClassBoKEntries(data).then(response => {
		let studentCbok = {};
		if (response && response.length) {
			response.forEach(assignment => {
				let aid = assignment.aid;
				studentCbok[aid] = assignment;
			});
		}
		model.set('studentCbok', studentCbok);
		return response;
	});
};

module.exports = {
	get,
	create,
	remove,
	edit,
	getStudentEntries
};
